/**
 * URS-F353 — Vista previa de un cuestionario de estudio antes de asignarlo
 * a una visita. Renderiza cada pregunta tal y como la verá el voluntario al
 * cumplimentar, pero con estado local efímero (las respuestas NO se guardan).
 *
 * Soporta los mismos tipos que VolunteersPanel.js: text, number, textarea,
 * date, select, scale, radio, multiselect, boolean, boolean_with_obs, nps,
 * likert (1-5 y 1-7), table_text, table_select, custom_table, derm_scale (stub),
 * masi (stub) y section_header.
 */
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { CustomTableRenderer } from '../CustomTable';
import { CompositeScaleRenderer } from '../CompositeScale';
import { Eye } from '@phosphor-icons/react';

export function QuestionnairePreviewDialog({ open, onClose, questionnaire, lang = 'es' }) {
  const [ans, setAns] = useState({});
  const set = (qid, v) => setAns(p => ({ ...p, [qid]: v }));
  const setSub = (qid, key, v) => setAns(p => ({ ...p, [qid]: { ...((typeof p[qid] === 'object' && p[qid]) || {}), [key]: v } }));

  // Logica condicional: refleja en la vista previa el salto de preguntas.
  const condMet = (q) => {
    const c = q?.condition;
    if (!c || !c.question_id) return true;
    return ans[c.question_id] === c.equals;
  };
  const qLabel = (qid) => {
    const f = (questionnaire?.questions || []).find(x => x.question_id === qid);
    return f ? (f.text_es || f.text_en || f.question_id) : qid;
  };

  if (!questionnaire) return null;
  const questions = questionnaire.questions || [];

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setAns({}); onClose(); } }}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col" data-testid="questionnaire-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye size={18} className="text-[#1f3e80]" />
            {lang === 'es' ? 'Vista previa del cuestionario' : 'Questionnaire preview'}
            <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200 ml-2">
              {lang === 'es' ? 'Las respuestas no se guardan' : 'Answers are not saved'}
            </Badge>
          </DialogTitle>
          <DialogDescription className="text-xs">
            <span className="font-semibold text-stone-700">{questionnaire.name}</span>
            {questionnaire.preamble_es && (
              <span className="block mt-2 text-stone-600 italic bg-violet-50/40 border border-violet-100 rounded-sm px-3 py-2">{questionnaire.preamble_es}</span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-3" data-testid="preview-scroll">
          {questions.length === 0 ? (
            <p className="text-center text-stone-400 text-sm py-8">{lang === 'es' ? 'Este cuestionario no tiene preguntas todavía.' : 'No questions yet.'}</p>
          ) : questions.map((q, idx) => {
            if (q.type === 'section_header') {
              return (
                <div key={q.question_id || `sh-${idx}`} className="border-t-2 border-violet-300 pt-3 mt-1" data-testid={`preview-section-${idx}`}>
                  <h4 className="text-sm font-bold uppercase tracking-wide text-violet-700">{q.title_es || q.text_es || ''}</h4>
                </div>
              );
            }
            const qid = q.question_id || `q-${idx}`;
            const v = ans[qid];
            const txt = q.text_es || q.text_en || q.text || '';
            if (!condMet(q)) return null;
            return (
              <div key={qid} data-testid={`preview-q-${qid}`} className="bg-white border border-stone-200 rounded-sm px-4 py-3">
                <p className="text-sm font-medium text-stone-800 mb-2">{txt}</p>
                {q.condition?.question_id && (
                  <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-0.5 mb-2 inline-block" data-testid={`preview-cond-${qid}`}>
                    {lang === 'es' ? 'Visible solo si' : 'Visible only if'} «{qLabel(q.condition.question_id)}» = {q.condition.equals === 'no' ? 'No' : (lang === 'es' ? 'Sí' : 'Yes')}
                  </p>
                )}

                {q.type === 'text' && (
                  <Input value={v || ''} onChange={e => set(qid, e.target.value)} className="bg-white" placeholder={lang === 'es' ? 'Escribir respuesta...' : 'Write answer...'} />
                )}
                {q.type === 'number' && (
                  <Input type="number" value={v || ''} onChange={e => set(qid, e.target.value)} className="bg-white w-40" />
                )}
                {q.type === 'textarea' && (
                  <Textarea value={v || ''} onChange={e => set(qid, e.target.value)} className="bg-white min-h-[60px]" />
                )}
                {q.type === 'date' && (
                  <Input type="date" value={v || ''} onChange={e => set(qid, e.target.value)} className="bg-white w-48" />
                )}
                {q.type === 'date_ongoing' && (() => {
                  const val = (typeof v === 'object' && v !== null) ? v : { date: (v || ''), ongoing: false };
                  const setVal = (patch) => set(qid, { ...val, ...patch });
                  return (
                    <div className="flex flex-wrap items-center gap-3">
                      <Input type="date" value={val.date || ''} disabled={!!val.ongoing} onChange={e => setVal({ date: e.target.value })} className="bg-white w-48 disabled:opacity-60" />
                      <label className="inline-flex items-center gap-1.5 text-xs text-stone-700 cursor-pointer select-none">
                        <input type="checkbox" checked={!!val.ongoing} onChange={e => setVal({ ongoing: e.target.checked, date: e.target.checked ? '' : val.date })} className="h-3.5 w-3.5 accent-[#1f3e80] cursor-pointer" />
                        <span>Continua (sin fecha de fin)</span>
                      </label>
                    </div>
                  );
                })()}
                {(q.type === 'select' || q.type === 'scale' || q.type === 'radio') && (
                  <div className="flex gap-1.5 flex-wrap">
                    {(q.options || []).map(opt => (
                      <button key={opt} type="button" onClick={() => set(qid, opt)}
                        className={`px-3 py-1 text-xs font-medium rounded-sm border transition-colors ${v === opt ? 'bg-[#1f3e80] text-white border-[#1f3e80]' : 'bg-white text-stone-600 border-stone-300 hover:bg-stone-50'}`}>
                        {opt}
                      </button>
                    ))}
                  </div>
                )}
                {q.type === 'multiselect' && (
                  <div className="flex gap-1.5 flex-wrap">
                    {(q.options || []).map(opt => {
                      const arr = Array.isArray(v) ? v : [];
                      const sel = arr.includes(opt);
                      return (
                        <button key={opt} type="button" onClick={() => set(qid, sel ? arr.filter(x => x !== opt) : [...arr, opt])}
                          className={`px-3 py-1 text-xs font-medium rounded-sm border transition-colors ${sel ? 'bg-[#1f3e80] text-white border-[#1f3e80]' : 'bg-white text-stone-600 border-stone-300'}`}>
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                )}
                {q.type === 'boolean' && (
                  <div className="flex gap-2">
                    {['si', 'no', 'na'].map(opt => (
                      <button key={opt} type="button" onClick={() => set(qid, opt)}
                        className={`px-4 py-1.5 text-xs font-medium rounded-sm border ${v === opt ? 'bg-[#1f3e80] text-white border-[#1f3e80]' : 'bg-white text-stone-600 border-stone-300'}`}>
                        {opt === 'si' ? (lang === 'es' ? 'Sí' : 'Yes') : opt === 'no' ? 'No' : (lang === 'es' ? 'No aplica' : 'N/A')}
                      </button>
                    ))}
                  </div>
                )}
                {q.type === 'boolean_with_obs' && (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      {['si', 'no', 'na'].map(opt => (
                        <button key={opt} type="button" onClick={() => set(qid, opt)}
                          className={`px-4 py-1.5 text-xs font-medium rounded-sm border ${v === opt ? (opt === 'si' ? 'bg-red-600 text-white border-red-600' : 'bg-[#1f3e80] text-white border-[#1f3e80]') : 'bg-white text-stone-600 border-stone-300'}`}>
                          {opt === 'si' ? (lang === 'es' ? 'Sí' : 'Yes') : opt === 'no' ? 'No' : (lang === 'es' ? 'No aplica' : 'N/A')}
                        </button>
                      ))}
                    </div>
                    {v === 'si' && (
                      <Textarea placeholder={lang === 'es' ? 'Observaciones (obligatorio)' : 'Observations (required)'} className="bg-white min-h-[50px]" />
                    )}
                  </div>
                )}
                {q.type === 'nps' && (
                  <div className="flex flex-wrap gap-1.5">
                    {[0,1,2,3,4,5,6,7,8,9,10].map(n => (
                      <button key={n} type="button" onClick={() => set(qid, String(n))}
                        className={`w-9 h-9 rounded-sm border text-sm font-semibold ${v === String(n) ? 'bg-[#1f3e80] text-white border-[#1f3e80]' : 'bg-white text-stone-700 border-stone-200'}`}>
                        {n}
                      </button>
                    ))}
                  </div>
                )}
                {(q.type === 'likert_1_5' || q.type === 'likert') && (() => {
                  const size = q.scale_size === 7 ? 7 : 5;
                  const nums = Array.from({ length: size }, (_, i) => i + 1);
                  const leftLabel = size === 7 ? 'Me disgusta mucho' : 'Totalmente en desacuerdo';
                  const rightLabel = size === 7 ? 'Me gusta mucho' : 'Totalmente de acuerdo';
                  return (
                    <div>
                      <div className={`grid gap-1.5 ${size === 7 ? 'grid-cols-7' : 'grid-cols-5'}`}>
                        {nums.map(n => (
                          <button key={n} type="button" onClick={() => set(qid, String(n))}
                            className={`py-2 rounded-sm border text-sm font-semibold ${v === String(n) ? 'bg-[#1f3e80] text-white border-[#1f3e80]' : 'bg-white text-stone-700 border-stone-200'}`}>
                            {n}
                          </button>
                        ))}
                      </div>
                      <div className="flex justify-between text-[10px] text-stone-400 px-1 mt-1">
                        <span>1 · {leftLabel}</span>
                        <span>{size} · {rightLabel}</span>
                      </div>
                    </div>
                  );
                })()}
                {q.type === 'table_text' && (
                  <div className="border border-stone-200 rounded-sm overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-stone-50">
                        <tr><th className="text-left font-semibold text-stone-700 px-3 py-2 w-2/5 border-b border-stone-200">&nbsp;</th><th className="text-left font-semibold text-stone-700 px-3 py-2 border-b border-stone-200">{lang === 'es' ? 'Respuesta' : 'Answer'}</th></tr>
                      </thead>
                      <tbody>
                        {(q.options || []).map((opt, oi) => {
                          const isSection = typeof opt === 'string' && opt.trim().startsWith('##');
                          if (isSection) {
                            return <tr key={`s-${oi}`}><td colSpan={2} className="bg-violet-50/70 border-t border-violet-200 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-violet-700">{opt.replace(/^##\s*/, '')}</td></tr>;
                          }
                          const cur = (typeof v === 'object' && v) || {};
                          return (
                            <tr key={opt + oi} className="border-t border-stone-100">
                              <td className="px-3 py-2 text-stone-700">{opt}</td>
                              <td className="px-3 py-2"><Input type={q.cell_type === 'number' ? 'number' : 'text'} value={cur[opt] || ''} onChange={e => setSub(qid, opt, e.target.value)} className="h-8" /></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                {q.type === 'table_select' && (
                  <div className="border border-stone-200 rounded-sm overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-stone-50">
                        <tr><th className="text-left font-semibold text-stone-700 px-3 py-2 w-2/5 border-b border-stone-200">&nbsp;</th><th className="text-left font-semibold text-stone-700 px-3 py-2 border-b border-stone-200">{lang === 'es' ? 'Respuesta' : 'Answer'}</th></tr>
                      </thead>
                      <tbody>
                        {(q.options || []).map((opt, oi) => {
                          const isSection = typeof opt === 'string' && opt.trim().startsWith('##');
                          if (isSection) {
                            return <tr key={`s-${oi}`}><td colSpan={2} className="bg-violet-50/70 border-t border-violet-200 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-violet-700">{opt.replace(/^##\s*/, '')}</td></tr>;
                          }
                          const cur = (typeof v === 'object' && v) || {};
                          return (
                            <tr key={opt + oi} className="border-t border-stone-100">
                              <td className="px-3 py-2 text-stone-700">{opt}</td>
                              <td className="px-3 py-2">
                                <select value={cur[opt] || ''} onChange={e => setSub(qid, opt, e.target.value)} className="bg-white border border-stone-200 rounded-sm h-8 text-sm px-2 w-full">
                                  <option value="">{lang === 'es' ? '— Selecciona —' : '— Select —'}</option>
                                  {(q.cell_options || []).map(co => <option key={co} value={co}>{co}</option>)}
                                </select>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                {q.type === 'custom_table' && (
                  <div className="space-y-2">
                    <CustomTableRenderer
                      question={q}
                      answers={(typeof v === 'object' && v) || {}}
                      onChange={next => set(qid, next)}
                      lang={lang}
                    />
                    {q.show_observations !== false && (
                      <div className="border border-stone-200 bg-stone-50/40 rounded-sm px-3 py-2 space-y-1">
                        <Label className="text-[11px] font-medium text-stone-600">{lang === 'es' ? 'Observaciones' : 'Observations'}</Label>
                        <Textarea placeholder={lang === 'es' ? 'Comentarios adicionales (opcional)...' : 'Additional comments (optional)...'} className="bg-white min-h-[50px] text-sm" />
                      </div>
                    )}
                  </div>
                )}
                {q.type === 'checklist' && (() => {
                  const cur = Array.isArray(v) ? v : [];
                  const items = (q.options || []).filter(o => !(typeof o === 'string' && o.trim().startsWith('##')));
                  return (
                    <div className="border border-stone-200 rounded-sm overflow-hidden">
                      {items.map((it, ii) => {
                        const checked = cur.includes(it);
                        return (
                          <label key={it + ii} className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-stone-50 ${ii > 0 ? 'border-t border-stone-100' : ''} ${checked ? 'bg-violet-50/40' : ''}`}>
                            <input type="checkbox" checked={checked} onChange={() => set(qid, checked ? cur.filter(x => x !== it) : [...cur, it])} className="w-4 h-4 rounded-sm border-stone-300 text-[#1f3e80] focus:ring-[#1f3e80] cursor-pointer" />
                            <span className="text-sm text-stone-700 select-none">{it}</span>
                          </label>
                        );
                      })}
                    </div>
                  );
                })()}
                {q.type === 'nps_multi' && (() => {
                  const subs = (q.options || []).filter(o => !(typeof o === 'string' && o.trim().startsWith('##')));
                  const cur = (typeof v === 'object' && v) || {};
                  return (
                    <div className="border border-stone-200 rounded-sm overflow-hidden">
                      {subs.map((sub, si) => (
                        <div key={sub + si} className={`flex flex-col gap-1.5 px-3 py-2.5 ${si > 0 ? 'border-t border-stone-100' : ''}`}>
                          <span className="text-sm text-stone-700">{sub}</span>
                          <div className="flex flex-wrap gap-1.5">
                            {[0,1,2,3,4,5,6,7,8,9,10].map(n => (
                              <button key={n} type="button" onClick={() => setSub(qid, sub, String(n))}
                                className={`w-9 h-9 rounded-sm border text-sm font-semibold ${cur[sub] === String(n) ? 'bg-[#1f3e80] text-white border-[#1f3e80]' : 'bg-white text-stone-700 border-stone-200'}`}>
                                {n}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
                {q.type === 'composite_scale' && (
                  <CompositeScaleRenderer
                    question={q}
                    answers={(typeof v === 'object' && v) || {}}
                    onChange={(next) => set(qid, next)}
                    lang={lang}
                  />
                )}
                {q.type === 'derm_scale' && (
                  <div className="bg-violet-50/40 border border-violet-200 rounded-sm px-3 py-2 text-xs text-violet-700">
                    {lang === 'es' ? 'Escala dermatológica' : 'Dermatological scale'}: <span className="font-semibold">{q.scale_id || '—'}</span>
                    <p className="text-[10px] mt-1 italic">{lang === 'es' ? 'En cumplimentación real se mostrarán los niveles y la imagen de referencia.' : 'Real fill shows scale levels + reference image.'}</p>
                  </div>
                )}
                {q.type === 'masi' && (
                  <div className="bg-amber-50 border border-amber-200 rounded-sm px-3 py-2 text-xs text-amber-700">
                    {lang === 'es' ? 'Escala MASI compuesta — 4 regiones x 3 componentes (preview simplificado).' : 'Composite MASI — 4 regions x 3 components (simplified preview).'}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
